import React, { Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import { updateSEO } from '../utils/seo_utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import '../css/CoreTechnologyPage.css';

class CoreTechnologyPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      visibleSections: {}
    };
    this.update_lang = this.update_lang.bind(this);
    this.sectionRefs = [];
    this.observerCallback = this.observerCallback.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    updateSEO('core_technology', this.state.lang, '/core-technology');
    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px'
    });
    this.sectionRefs.forEach(ref => {
      if (ref) this.observer.observe(ref);
    });
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  observerCallback(entries) {
    const updates = {};
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        updates[entry.target.dataset.section] = true;
      }
    });
    if (Object.keys(updates).length > 0) {
      this.setState(prev => ({ visibleSections: { ...prev.visibleSections, ...updates } }));
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  update_lang(newLang) {
    this.props.updateLang(newLang);
  }

  navigate(route) {
    window.location.href = buildPath(this.state.lang, route);
  }

  registerRef(el, id) {
    if (el && !this.sectionRefs.includes(el)) {
      el.dataset.section = id;
      this.sectionRefs.push(el);
      if (this.observer) this.observer.observe(el);
    }
  }

  render() {
    const lang = this.state.lang;
    // zh-first: en/ja gracefully fall back to zh until they are localized.
    const t = (obj) => (obj && (obj[lang] || obj.zh)) || '';
    const vis = this.state.visibleSections;

    const hero = {
      tag: { zh: "AI ROBOTICS", en: "AI ROBOTICS", ja: "AI ROBOTICS" },
      title: { zh: "核心技術", en: "Core Technology", ja: "コア技術" },
      subtitle: {
        zh: "以 Agentic AI 統合從多模態感測、控制、決策到自主學習的完整技術鏈條",
        en: "Unifying Sensing, Control, and Autonomous Learning via Agentic AI.",
        ja: "エージェンティックAIが実現する、センシング・制御から自律学習までの完全統合"
      }
    };

    const introText = {
      zh: "Nexuni 專注研發核心 AI 大腦以驅動各式機器人自動化硬體，加速產業自動化落地。我們透過此大腦統合多模態感測與控制，形成自主決策及持續學習的完整流程。結合自研的邊緣運算與模組化策略，我們完美融合 Agentic AI 的通用性與自研機器人控制邏輯的確定性，讓機器人能理解語言指令並適應各式環境，快速部署至全新場域，自主靈活地處理各類動態任務。",
      en: "Nexuni develops the core AI brain that drives robots of every kind, accelerating industrial automation. The brain unifies multimodal sensing and control into one loop of autonomous decision-making and continuous learning. With in-house edge computing and a modular strategy, we fuse the generality of Agentic AI with the determinism of our own control logic — so robots understand language, adapt to any environment, deploy fast to new sites, and handle dynamic tasks on their own.",
      ja: "Nexuniは、あらゆるロボットの自動化ハードウェアを駆動するコアAIブレインの研究開発に注力し、産業の自動化を加速します。このブレインはマルチモーダル感知と制御を統合し、自律的な意思決定と継続学習の一連の流れを形成します。自社開発のエッジコンピューティングとモジュール化戦略により、Agentic AIの汎用性と自社制御ロジックの確定性を融合し、ロボットが言語指示を理解し、あらゆる環境に適応し、新しい現場へ迅速に導入され、動的なタスクを自律的に処理できるようにします。"
    };

    // ---- Perception input icons (multimodal) ----
    const icon = {
      lang: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>,
      voice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0014 0"/><line x1="12" y1="17" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></svg>,
      vision: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
      thermal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z"/></svg>,
      gas: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
      locate: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
    };

    const niro = {
      eyebrow: { zh: "NIRO平台", en: "NIRO PLATFORM", ja: "NIROプラットフォーム" },
      headline: { zh: "一套大腦驅動各式機器人硬體", en: "One brain powers every robot.", ja: "ひとつの頭脳であらゆるロボットを駆動" },
      sub: {
        zh: "NIRO 用軟體驅動任何機器人與自動化硬體：將語言指令與多模態感知，拆解、編排成機器人可執行的精準動作。",
        en: "NIRO drives any robot or automation hardware in software, turning language and multimodal perception into precise, executable actions.",
        ja: "NIROはソフトウェアであらゆるロボット・自動化ハードウェアを駆動し、言語指示とマルチモーダル感知を、ロボットが実行できる精密な動作へ分解・編成します。"
      },
      perceptionLabel: { zh: "多模態感知輸入", en: "PERCEPTION INPUT", ja: "マルチモーダル感知入力" },
      perception: [
        { icon: icon.lang, name: { zh: "自然語言", en: "Language", ja: "自然言語" } },
        { icon: icon.voice, name: { zh: "語音", en: "Voice", ja: "音声" } },
        { icon: icon.vision, name: { zh: "影像", en: "Vision", ja: "映像" } },
        { icon: icon.thermal, name: { zh: "熱影像", en: "Thermal", ja: "熱画像" } },
        { icon: icon.gas, name: { zh: "毒氣偵測", en: "Gas", ja: "ガス検知" } },
        { icon: icon.locate, name: { zh: "定位", en: "Localization", ja: "測位" } }
      ],
      coreModel: { zh: "大型AI 模型 (Gemini/Claude/ChatGPT/地端) · 任一模型、即插即用", en: "Large AI models (Gemini/Claude/ChatGPT/on-prem) · any model, plug-and-play", ja: "大規模AIモデル（Gemini/Claude/ChatGPT/オンプレ）· どのモデルでも即接続" },
      coreHarness: { zh: "AI Harness for Robotics", en: "AI Harness for Robotics", ja: "AI Harness for Robotics" },
      coreLines: [
        { zh: "Agentic AI · 任務拆解 × 工具調度", en: "Agentic AI · task decomposition × tool routing", ja: "Agentic AI · タスク分解 × ツール調整" }
      ],
      coreSkills: { zh: "技能：移動導航 · 焊接控制 · 影像辨識 ＋ 客製化擴充", en: "Skills: navigation · welding · vision + custom expansion", ja: "スキル：移動ナビ · 溶接制御 · 画像認識 ＋ カスタム拡張" },
      embodimentLabel: { zh: "驅動全產線", en: "EMBODIMENT", ja: "適用ロボット" },
      embodiment: [
        { name: { zh: "智慧停車場", en: "Smart Parking", ja: "スマート駐車場" } },
        { name: { zh: "智慧焊接", en: "Welding Robot", ja: "溶接ロボット" } },
        { name: { zh: "巡檢機器狗", en: "Patrol Dog", ja: "巡回ロボット犬" } },
        { name: { zh: "雙臂機器人", en: "Dual-Arm", ja: "双腕ロボット" } }
      ],
      callouts: [
        { title: { zh: "通用機器人大腦", en: "Universal robot brain", ja: "汎用ロボット頭脳" }, desc: { zh: "驅動任何機器人硬體", en: "Drives any robot hardware", ja: "あらゆるロボットハードウェアを駆動" } },
        { title: { zh: "機器人專屬AI Harness", en: "Robotics-native AI Harness", ja: "ロボット専用AI Harness" }, desc: { zh: "統合多模態感知與機器人控制", en: "Unifies multimodal sensing and robot control", ja: "マルチモーダル感知とロボット制御を統合" } },
        { title: { zh: "獨立安全核心", en: "Independent safety core", ja: "独立した安全コア" }, desc: { zh: "硬體級 Fail-safe 即時中斷", en: "Hardware-level fail-safe, instant cutoff", ja: "ハードウェア級フェイルセーフで即時遮断" } }
      ]
    };

    const mainlines = {
      eyebrow: { zh: "三大機器人研發方向", en: "THREE R&D DIRECTIONS", ja: "3大研究開発の方向性" },
      headline: { zh: "掌握運動控制與感知核心，以 AI 智慧決策建構越部署越聰明的資料飛輪。", en: "AI orchestrates robot movement and perception and learns to act smarter with every deployment.", ja: "AIがロボットの運動と感知を統括し、導入を重ねるごとに、より賢く行動することを学習します。" },
      lines: [
        {
          key: "MOTION",
          color: "var(--main-color)",
          title: { zh: "運動控制", en: "Motion Control", ja: "運動制御" },
          desc: { zh: "自研即時控制核心: 確定性控制、低延遲，工業級穩定性。", en: "In-house real-time control core: deterministic control, low latency, industrial-grade stability.", ja: "自社開発のリアルタイム制御コア：確定的な制御、低遅延、工業級の安定性。" },
          tags: [
            { zh: "6 軸手臂", en: "6-axis arm", ja: "6軸アーム" },
            { zh: "12 馬達四足", en: "12-motor quadruped", ja: "12モーター四足" },
            { zh: "雙臂協作", en: "Dual-arm", ja: "双腕協調" }
          ],
          bullets: {
            zh: [
              "整合感測、運動控制、定位（視覺 SLAM、RTK GPS、IMU 融合）與路徑規劃，適應室內外動態環境",
              "強化學習訓練四足與人形機器人運動控制，在複雜地形中穩定行走與平衡恢復"
            ],
            en: [
              "Integrates sensing, motion control, localization (visual SLAM, RTK GPS, IMU fusion) and path planning for dynamic indoor/outdoor environments",
              "Reinforcement learning for quadruped and humanoid locomotion — stable walking and balance recovery on complex terrain"
            ],
            ja: [
              "センシング・運動制御・測位（視覚SLAM、RTK GPS、IMU融合）・経路計画を統合し、屋内外の動的環境に対応",
              "強化学習で四足・ヒューマノイドの運動制御を訓練し、複雑地形での安定歩行とバランス回復を実現"
            ]
          }
        },
        {
          key: "PERCEPTION",
          color: "var(--sub2-color)",
          title: { zh: "感知系統", en: "Perception", ja: "感知システム" },
          desc: { zh: "整合視覺、光學、氣體等多模態感測，並透過架設 AI 自動標註流程，將原始感知資料高效轉化為高品質訓練資料集。", en: "Integrates vision, optical, and gas multimodal sensing, and by building an AI auto-labeling workflow, efficiently turns raw perception data into high-quality training datasets.", ja: "視覚・光学・ガスなどのマルチモーダル感知を統合し、AI自動アノテーションのフローを構築することで、生の感知データを高品質な学習データセットへ効率的に変換します。" },
          tags: [
            { zh: "多模態感測", en: "Multimodal sensing", ja: "マルチモーダル感知" },
            { zh: "AI 自動標註", en: "AI auto-labeling", ja: "AI自動アノテーション" },
            { zh: "NIR · 毒氣 · 熱影像", en: "NIR · Gas · Thermal", ja: "NIR · ガス · 熱画像" }
          ],
          bullets: {
            zh: [
              "多感測器同步紀錄（RGBD、LiDAR、IMU）搭配自研標註與資料處理管線，持續累積高品質資料集",
              "關鍵光學與氣體感測自主研發，深入掌握高危場域的感知能力"
            ],
            en: [
              "Synchronized multi-sensor logging (RGBD, LiDAR, IMU) with in-house annotation and data pipelines to build high-quality datasets",
              "In-house optical and gas sensing for deep perception in high-risk environments"
            ],
            ja: [
              "マルチセンサー同期記録（RGBD、LiDAR、IMU）と自社アノテーション・データ処理基盤で高品質データセットを蓄積",
              "重要な光学・ガスセンシングを自社開発し、高リスク現場の感知能力を掌握"
            ]
          }
        },
        {
          key: "INTELLIGENCE",
          color: "var(--sub1-color)",
          title: { zh: "決策智慧", en: "Decision Intelligence", ja: "意思決定知能" },
          desc: { zh: "開發專屬於機器人的Agentic AI，讓大模型直接理解任務、生成動作，免大量訓練資料即可導入。", en: "We build robotics-native Agentic AI so large models understand tasks and generate actions directly — deployable without massive training data.", ja: "ロボット専用のAgentic AIを開発し、大規模モデルがタスクを直接理解して動作を生成。大量の学習データなしで導入できます。" },
          tags: [
            "VLA",
            "Agentic AI for Robots",
            { zh: "NIRO 編排", en: "NIRO orchestration", ja: "NIRO編成" }
          ],
          bullets: {
            zh: [
              "AI 大模型實現操作任務的自主規劃，理解語言指令並生成動作",
              "可結合VLA從少量人類示範快速習得技能，大幅縮短部署時間"
            ],
            en: [
              "Large AI models autonomously plan manipulation tasks, understanding language and generating actions",
              "Combine VLA to acquire skills from just a few human demonstrations, greatly cutting deployment time"
            ],
            ja: [
              "AI大規模モデルが操作タスクを自律的に計画し、言語指示を理解して動作を生成",
              "VLAと組み合わせ、少数の人間のデモから素早くスキルを習得し、導入時間を大幅に短縮"
            ]
          }
        }
      ],
      flywheelLabel: { zh: "資料飛輪", en: "DATA FLYWHEEL", ja: "データフライホイール" },
      flywheel: [
        { zh: "真實場域資料", en: "Real-world data", ja: "実環境データ" },
        { zh: "數位學生模擬", en: "Digital-twin simulation", ja: "デジタルツイン模擬" },
        { zh: "可靠度持續改善", en: "Reliability gains", ja: "信頼性の継続改善" },
        { zh: "規模化部署", en: "Scaled deployment", ja: "スケール展開" }
      ],
      flywheelNote: { zh: "越部署越聰明", en: "smarter with every deployment", ja: "使うほど賢く" }
    };

    const safety = {
      eyebrow: { zh: "安全可驗證的AI機器人控制", en: "VERIFIABLE AI ROBOT CONTROL", ja: "検証可能なAIロボット制御" },
      headline: { zh: "產出可驗證及可做安全把關的機器人程式", en: "Generating robot programs that are verifiable and safety-gated.", ja: "検証可能で安全ゲートをかけられるロボットプログラムを生成。" },
      sub: {
        zh: "我們把安全迴路與 AI 邏輯完全分離。AI 負責思考與彈性決策；獨立安全核心以硬體級確定性，在毫秒間中斷任何異常。",
        en: "We fully decouple the safety loop from AI logic. The AI thinks and makes flexible decisions; an independent safety core, deterministic at the hardware level, interrupts any anomaly within milliseconds.",
        ja: "安全ループとAIロジックを完全に分離。AIは思考と柔軟な意思決定を担い、独立した安全コアがハードウェア級の確定性であらゆる異常をミリ秒で遮断します。"
      },
      aiLayer: {
        tag: { zh: "AI 智慧層", en: "AI LAYER", ja: "AI知能レイヤー" },
        title: { zh: "通用式智慧理解· 依場域狀況彈性決策", en: "General intelligence · context-aware flexible decisions", ja: "汎用的な知能理解 · 現場状況に応じた柔軟な意思決定" },
        desc: { zh: "用AI整合多模態感知理解任務、規劃動作、適應環境", en: "Uses AI to fuse multimodal sensing — understanding tasks, planning actions, adapting to the environment.", ja: "AIでマルチモーダル感知を統合し、タスクを理解して動作を計画し、環境に適応。" }
      },
      sepLabel: { zh: "完全分離", en: "DECOUPLED", ja: "完全分離" },
      safetyLayer: {
        tag: { zh: "獨立安全核心", en: "SAFETY CORE", ja: "安全コア" },
        title: { zh: "確定性 · 硬體級", en: "Deterministic, hardware-level", ja: "確定的 · ハードウェア級" },
        desc: { zh: "急停與區域防護直接觸發硬體斷電；AI 異常由獨立 Watchdog 毫秒級中斷，依循 ISO 10218 設計。", en: "E-stop and zone protection trip power directly; an independent watchdog interrupts AI anomalies in milliseconds, designed to ISO 10218.", ja: "非常停止と領域防護がハードウェアを直接遮断。AI異常は独立Watchdogがミリ秒で中断し、ISO 10218に準拠して設計。" }
      },
      columns: [
        {
          key: "SECURITY",
          title: { zh: "地端資安", en: "Edge Security", ja: "オンプレ・セキュリティ" },
          desc: { zh: "AI 推論 100% 在廠內邊緣主機執行，支援完全離線運作；數據僅存於廠內、存取皆留紀錄，軟體更新依客戶資安政策以離線簽章套件套用。", en: "100% on-prem edge inference, fully offline-capable; data stays in-plant with audited access and signed offline updates.", ja: "AI推論は100%工場内エッジで実行し完全オフライン対応。データは構内のみに保存しアクセスは記録、ソフト更新は顧客の方針に従い署名済みオフラインパッケージで適用。" },
          tags: [
            { zh: "全本地運算", en: "Fully local", ja: "完全ローカル" },
            { zh: "數據不出廠", en: "Data stays on-site", ja: "データ非持出" },
            { zh: "離線可運作", en: "Offline-capable", ja: "オフライン動作" }
          ]
        },
        {
          key: "SAFETY",
          title: { zh: "獨立安全核心", en: "Independent Safety Core", ja: "独立した安全コア" },
          desc: { zh: "安全迴路與 AI 邏輯完全分離：急停與區域防護直接觸發硬體斷電，AI 異常由獨立 Watchdog 毫秒級中斷；依循 ISO 10218 設計並附風險評估。", en: "Safety loop fully separated from AI: hardware power-off on e-stop and zone breach, millisecond watchdog interrupts, ISO 10218 with risk assessment.", ja: "安全ループとAIロジックを完全分離。非常停止と領域防護がハードウェアを直接遮断し、AI異常は独立Watchdogがミリ秒で中断。ISO 10218準拠でリスクアセスメント付き。" },
          tags: [
            { zh: "硬體級斷電", en: "HW power-off", ja: "ハード遮断" },
            "Fail-safe",
            "ISO 10218"
          ]
        },
        {
          key: "RELIABILITY",
          title: { zh: "穩定性控制", en: "Reliable Control", ja: "安定性制御" },
          desc: { zh: "自研即時控制核心：控制週期毫秒級、行為可預期；Watchdog 監控異常自動降轉安全狀態，出廠前經連續運轉驗證。", en: "Real-time control core with millisecond cycles and predictable behavior; a watchdog auto-degrades to a safe state, burn-in tested before shipping.", ja: "自社開発のリアルタイム制御コア：制御周期はミリ秒級で挙動は予測可能。Watchdogが異常を監視し自動で安全状態へ降格、出荷前に連続運転で検証。" },
          tags: [
            { zh: "毫秒級控制週期", en: "ms control cycle", ja: "ミリ秒制御周期" },
            { zh: "行為可預期", en: "Predictable", ja: "予測可能" },
            { zh: "異常自恢復", en: "Auto-recovery", ja: "異常自己回復" }
          ]
        }
      ],
      traceLabel: { zh: "品質可追溯", en: "QUALITY TRACEABILITY", ja: "品質トレーサビリティ" },
      trace: [
        { zh: "全程影像＋參數記錄", en: "Full video + parameter logging", ja: "全工程の映像＋パラメータ記録" },
        { zh: "AI 即時品質驗證", en: "Real-time AI QA", ja: "AIリアルタイム品質検証" },
        { zh: "異常自動告警", en: "Auto anomaly alerts", ja: "異常の自動アラート" },
        { zh: "稽核紀錄可匯出", en: "Exportable audit trail", ja: "監査ログをエクスポート可能" }
      ],
      traceNote: { zh: "每一步可驗證", en: "every step verifiable", ja: "各ステップが検証可能" }
    };

    const patents = {
      title: { zh: "研發成果、專利及認證", en: "Research Achievements, Patents & Certifications", ja: "研究成果、特許および認証" },
      desc: {
        zh: "我們的團隊擁有強大的研發實力，至今已在台灣、美國及新加坡獲得超過十五項專利，涵蓋機器人技術、自動化系統和人工智慧等領域。我們已獲得 Universal Robots 認證並入列 NVIDIA Inception，跨國場域累計部署超過百套系統。",
        en: "Our team has obtained over fifteen patents in Taiwan, the United States, and Singapore, covering robotics, automation systems, and AI. We are a Certified Solution Partner of Universal Robots and an NVIDIA Inception member, with 100+ systems deployed across borders.",
        ja: "私たちのチームは台湾、米国、シンガポールで15件以上の特許を取得し、ロボット技術・自動化システム・AIをカバーしています。Universal Robots の認定ソリューションパートナーであり、NVIDIA Inception メンバーとして、各国の現場で100台以上のシステムを導入しています。"
      },
      stats: [
        { num: "15+", label: { zh: "項專利", en: "Patents", ja: "件の特許" } },
        { num: "100+", label: { zh: "套已部署", en: "Deployed", ja: "システム導入" } },
        { num: "88%", label: { zh: "人力節省", en: "Labor Saved", ja: "人件費削減" } },
        { num: "+150%", label: { zh: "生產力提升", en: "Productivity", ja: "生産性向上" } }
      ]
    };

    const ctaText = { zh: "了解我們的產品", en: "Explore Our Products", ja: "製品を見る" };

    return (
      <div id="body" className="coretech_page">
        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb lang={lang} items={[{ label: { zh: "核心技術", en: "Core Technology", ja: "コア技術" } }]} />

        {/* Hero */}
        <section className="ct_hero">
          <div className="ct_hero_grid" />
          <div className="ct_hero_content">
            <span className="ct_hero_tag">{hero.tag[lang]}</span>
            <h1 className="ct_hero_title">{hero.title[lang]}</h1>
            <p className="ct_hero_subtitle">{hero.subtitle[lang]}</p>
          </div>
          <div className="ct_hero_accent" />
        </section>

        {/* Intro */}
        <section
          className={"ct_intro" + (vis["intro"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "intro")}
        >
          <div className="ct_intro_inner">
            <div className="ct_intro_line" />
            <p className="ct_intro_text">{introText[lang]}</p>
          </div>
        </section>

        {/* ===== NIRO Platform — the AI brain ===== */}
        <section
          className={"ct_niro" + (vis["niro"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "niro")}
        >
          <div className="ct_niro_inner">
            <div className="ct_section_head">
              <span className="ct_eyebrow">{t(niro.eyebrow)}</span>
              <h2 className="ct_section_headline">{t(niro.headline)}</h2>
              <p className="ct_section_sub">{t(niro.sub)}</p>
            </div>

            <div className="ct_niro_flow">
              {/* Perception input */}
              <div className="ct_niro_col ct_niro_io">
                <span className="ct_niro_col_label">{t(niro.perceptionLabel)}</span>
                <div className="ct_niro_io_grid">
                  {niro.perception.map((p, i) => (
                    <div className="ct_niro_chip" key={i}>
                      <span className="ct_niro_chip_icon">{p.icon}</span>
                      <span>{t(p.name)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <span className="ct_niro_arrow" aria-hidden="true">&rsaquo;</span>

              {/* NIRO core */}
              <div className="ct_niro_col ct_niro_core_col">
                <span className="ct_niro_core_model">{t(niro.coreModel)}</span>
                <div className="ct_niro_core">
                  <div className="ct_niro_core_glow" />
                  <span className="ct_niro_core_harness">{t(niro.coreHarness)}</span>
                  <span className="ct_niro_core_name">NIRO</span>
                  <span className="ct_niro_core_full">Nexuni Intelligent Robot Orchestration</span>
                  <div className="ct_niro_core_lines">
                    {niro.coreLines.map((l, i) => (
                      <span key={i}>{t(l)}</span>
                    ))}
                  </div>
                  <span className="ct_niro_core_skills">{t(niro.coreSkills)}</span>
                </div>
              </div>

              <span className="ct_niro_arrow" aria-hidden="true">&rsaquo;</span>

              {/* Embodiment */}
              <div className="ct_niro_col ct_niro_io">
                <span className="ct_niro_col_label">{t(niro.embodimentLabel)}</span>
                <div className="ct_niro_embody">
                  {niro.embodiment.map((e, i) => (
                    <div className="ct_niro_embody_item" key={i}>{t(e.name)}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ct_niro_callouts">
              {niro.callouts.map((c, i) => (
                <div className="ct_niro_callout" key={i}>
                  <span className="ct_niro_callout_mark" />
                  <h4>{t(c.title)}</h4>
                  <p>{t(c.desc)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Three R&D Mainlines ===== */}
        <section
          className={"ct_lines" + (vis["lines"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "lines")}
        >
          <div className="ct_lines_inner">
            <div className="ct_section_head">
              <span className="ct_eyebrow">{t(mainlines.eyebrow)}</span>
              <h2 className="ct_section_headline">{t(mainlines.headline)}</h2>
            </div>

            <div className="ct_lines_grid">
              {mainlines.lines.map((l, i) => (
                <div className="ct_line_card" key={i} style={{ '--line-color': l.color }}>
                  <span className="ct_line_key">{l.key}</span>
                  <h3 className="ct_line_title">{t(l.title)}</h3>
                  <p className="ct_line_desc">{t(l.desc)}</p>
                  <div className="ct_line_tags">
                    {l.tags.map((tag, ti) => (
                      <span className="ct_line_tag" key={ti}>{typeof tag === 'string' ? tag : t(tag)}</span>
                    ))}
                  </div>
                  <ul className="ct_line_bullets">
                    {t(l.bullets).map((b, bi) => (
                      <li key={bi}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="ct_flow_strip">
              <span className="ct_flow_label">{t(mainlines.flywheelLabel)}</span>
              <div className="ct_flow_steps">
                {mainlines.flywheel.map((s, i) => (
                  <React.Fragment key={i}>
                    <span className="ct_flow_step">{t(s)}</span>
                    {i < mainlines.flywheel.length - 1 && <span className="ct_flow_sep" aria-hidden="true">&rsaquo;</span>}
                  </React.Fragment>
                ))}
              </div>
              <span className="ct_flow_note">{t(mainlines.flywheelNote)}</span>
            </div>
          </div>
        </section>

        {/* ===== Industrial-grade safety ===== */}
        <section
          className={"ct_safety" + (vis["safety"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "safety")}
        >
          <div className="ct_safety_inner">
            <div className="ct_section_head">
              <span className="ct_eyebrow">{t(safety.eyebrow)}</span>
              <h2 className="ct_section_headline">{t(safety.headline)}</h2>
              <p className="ct_section_sub">{t(safety.sub)}</p>
            </div>

            {/* Decoupling diagram: AI layer  ‖  independent safety core */}
            <div className="ct_safety_split">
              <div className="ct_safety_layer ct_safety_ai">
                <span className="ct_safety_layer_tag">{t(safety.aiLayer.tag)}</span>
                <h4>{t(safety.aiLayer.title)}</h4>
                <p>{t(safety.aiLayer.desc)}</p>
              </div>
              <div className="ct_safety_sep">
                <span className="ct_safety_sep_label">{t(safety.sepLabel)}</span>
              </div>
              <div className="ct_safety_layer ct_safety_core">
                <span className="ct_safety_layer_tag">{t(safety.safetyLayer.tag)}</span>
                <h4>{t(safety.safetyLayer.title)}</h4>
                <p>{t(safety.safetyLayer.desc)}</p>
              </div>
            </div>

            <div className="ct_safety_cols">
              {safety.columns.map((c, i) => (
                <div className="ct_safety_col" key={i}>
                  <span className="ct_safety_col_key">{c.key}</span>
                  <h3 className="ct_safety_col_title">{t(c.title)}</h3>
                  <p className="ct_safety_col_desc">{t(c.desc)}</p>
                  <div className="ct_safety_tags">
                    {c.tags.map((tag, ti) => (
                      <span className="ct_safety_tag" key={ti}>{typeof tag === 'string' ? tag : t(tag)}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="ct_flow_strip ct_flow_strip_alt">
              <span className="ct_flow_label">{t(safety.traceLabel)}</span>
              <div className="ct_flow_steps">
                {safety.trace.map((s, i) => (
                  <React.Fragment key={i}>
                    <span className="ct_flow_step">{t(s)}</span>
                    {i < safety.trace.length - 1 && <span className="ct_flow_sep" aria-hidden="true">&rsaquo;</span>}
                  </React.Fragment>
                ))}
              </div>
              <span className="ct_flow_note">{t(safety.traceNote)}</span>
            </div>
          </div>
        </section>

        {/* Patents & Research */}
        <section
          className={"ct_patents" + (vis["patents"] ? " ct_visible" : "")}
          ref={el => this.registerRef(el, "patents")}
        >
          <div className="ct_patents_inner">
            <h2 className="ct_patents_title">{patents.title[lang]}</h2>

            <div className="ct_patents_stats">
              {patents.stats.map((s, i) => (
                <div key={i} className="ct_stat_item">
                  <span className="ct_stat_num">{s.num}</span>
                  <span className="ct_stat_label">{s.label[lang]}</span>
                </div>
              ))}
            </div>

            <p className="ct_patents_desc">{patents.desc[lang]}</p>
          </div>
        </section>

        {/* CTA */}
        <section className="ct_cta">
          <button className="ct_cta_btn" onClick={() => this.navigate('/solutions')}>
            {ctaText[lang]} &rarr;
          </button>
        </section>

        <Footer lang={lang} navigate={(route) => this.navigate(route)} />
      </div>
    );
  }
}

export default CoreTechnologyPage;
